import {
  COMMUNITY_ROLES_FETCH_FAILURE,
  COMMUNITY_ROLES_FETCH_REQUEST,
  COMMUNITY_ROLES_FETCH_SUCCESS,
  COMMUNITY_ROLES_UPDATE_FAILURE,
  COMMUNITY_ROLES_UPDATE_REQUEST,
  COMMUNITY_ROLES_UPDATE_SUCCESS,
} from '../../types';
import {
  getProgramEnrollmentsApi,
  updateEnrollmentModeratedChannelsApi,
  updateEnrollmentVisibilityApi,
  updateEnrollmentRoleApi,
} from '../../api';
import { getUserProgramEnrollmentData } from './programEnrollmentAction';
import { getEnrolledChannels } from './programAction';
import { ICommunityRoleUser } from '../../types/communityRoles';

const normalizeRole = (role: any): string => {
  const value = (role ?? '').toString().trim();
  const lower = value.toLowerCase();
  if (lower === 'admin') return 'CommunityAdmin';
  if (lower === 'team-admin') return 'CommunityAdmin';
  if (lower === 'community_admin') return 'CommunityAdmin';
  if (lower === 'communityadmin') return 'CommunityAdmin';
  if (lower === 'coach') return 'moderator';
  if (lower === 'communitymoderator') return 'moderator';
  if (lower === 'service account' || lower === 'firmware-admin') return 'admin';
  return value || 'member';
};

const normalizeEnrollments = (payload: any[]): ICommunityRoleUser[] => {
  if (!Array.isArray(payload)) return [];
  return payload.map((item: any) => ({
    _id: item?._id,
    programId: item?.programId,
    userId: typeof item?.userId === 'object' ? item?.userId?._id : item?.userId,
    user: item?.userId,
    communityIdentifier: item?.communityIdentifier,
    role: normalizeRole(item?.role),
    visibleChannelIds: item?.visibleChannelIds || [],
    moderatedChannels: item?.moderatedChannels || [],
    channels: item?.channels || [],
  }));
};

export const fetchCommunityEnrollments = (programId: string, communityIdentifier?: string) => async (dispatch: any) => {
  dispatch({ type: COMMUNITY_ROLES_FETCH_REQUEST });
  try {
    const response: any = await getProgramEnrollmentsApi(programId, communityIdentifier);
    const enrollments = normalizeEnrollments(response);
    dispatch({
      type: COMMUNITY_ROLES_FETCH_SUCCESS,
      payload: {
        enrollments,
        communityIdentifier,
      },
    });
  } catch (error) {
    const err = error as any;
    dispatch({
      type: COMMUNITY_ROLES_FETCH_FAILURE,
      payload: err?.response?.data?.message || err?.message || 'Failed to load enrollments',
    });
  }
};

export const updateCommunityMemberAccess = (params: {
  programId: string;
  userId: string;
  communityIdentifier: string;
  visibleChannelIds: string[];
  moderatedChannels: Array<{ channelIdentifier: string; role?: string; isActive?: boolean }>;
  role?: string;
}) => async (dispatch: any) => {
  const { programId, userId, communityIdentifier, visibleChannelIds, moderatedChannels, role } = params;
  dispatch({ type: COMMUNITY_ROLES_UPDATE_REQUEST, payload: { userId } });
  try {
    const normalizedRoleValue = role === 'admin' ? 'CommunityAdmin' : role;

    const [visibilityRes, moderatedRes] = await Promise.all([
      updateEnrollmentVisibilityApi(programId, userId, communityIdentifier, visibleChannelIds),
      updateEnrollmentModeratedChannelsApi(programId, userId, communityIdentifier, moderatedChannels),
    ]);

    let resolvedRole: string | undefined;
    if (typeof normalizedRoleValue === 'string' && normalizedRoleValue.trim().length > 0) {
      const roleRes: any = await updateEnrollmentRoleApi(programId, userId, communityIdentifier, normalizedRoleValue);
      resolvedRole = roleRes?.data?.role || normalizedRoleValue;
    }

    dispatch({
      type: COMMUNITY_ROLES_UPDATE_SUCCESS,
      payload: {
        userId,
        visibleChannelIds: visibilityRes?.data?.visibleChannelIds || visibleChannelIds,
        moderatedChannels: moderatedRes?.data?.moderatedChannels || moderatedChannels,
        role: resolvedRole ? normalizeRole(resolvedRole) : undefined,
      },
    });

    dispatch(getUserProgramEnrollmentData(programId, userId));
    dispatch(getEnrolledChannels(programId, userId, communityIdentifier));
  } catch (error) {
    const err = error as any;
    dispatch({
      type: COMMUNITY_ROLES_UPDATE_FAILURE,
      payload: {
        userId,
        error: err?.response?.data?.message || err?.message || 'Failed to update access',
      },
    });
    throw error;
  }
};
